<template>
  <app-page-container>
    <app-spinner v-if="!is_loaded" />
    <template v-else-if="load_error">
      <p>Reservations could not be loaded.</p>
      <app-button @click.native="fetchAllLists">Retry</app-button>
    </template>
    <template v-else>
      <h2 class="title title--mb15">Hotels</h2>
      <template v-if="hotels_reservations.length">
        <AppAccHotel
          v-for="(hotel, hotels_index) in hotels_reservations"
          :key="`hotel_acc_${hotels_index}`"
          :reservation="hotel"
          :order-number="hotels_index + 1"
          is-name-link-disable
        />
      </template>
      <app-no-results v-else text="There’s no reservations yet" />
      <section>
        <h2 class="title title--mb15 title--mt30">Extra meals</h2>
        <p class="meals-description">Includes standalone orders and meals ordered with a hotel reservation. Check each meal’s date and guests. Awaiting confirmation means the meal is not yet confirmed.</p>
        <app-cells-grid v-if="active_meals.length" :columns="1" gap="10px">
          <AppAccMeal
            v-for="(meal, index) in active_meals"
            :key="`meal_${meal.id}`"
            :reservation="meal"
            :order-number="index + 1"
            :warning="mealWarning(meal)"
            show-meal-context
            is-name-link-disable
          />
        </app-cells-grid>
        <app-no-results v-else text="There are no active extra meals" small />
        <details v-if="cancelled_meals.length" class="cancelled-meals">
          <summary>Cancelled meals ({{ cancelled_meals.length }})</summary>
          <app-cells-grid :columns="1" gap="10px">
            <AppAccMeal
              v-for="(meal, index) in cancelled_meals"
              :key="`cancelled_meal_${meal.id}`"
              :reservation="meal"
              :order-number="index + 1"
              show-meal-context
              is-name-link-disable
            />
          </app-cells-grid>
        </details>
      </section>
      <template v-if="flights_reservations.length">
        <h2 class="title title--mb15 title--mt30">Flights</h2>
        <AppAccFlight
          v-for="(flight, flights_index) in flights_reservations"
          :key="`flight_${flights_index}`"
          :reservation="flight"
          :order-number="flights_index + 1"
          is-name-link-disable
        />
      </template>
    </template>
  </app-page-container>
</template>

<script>
import AppAccFlight from '@/components/AppAccFlight.vue'
import AppAccHotel from '@/components/AppAccHotel.vue'
import AppAccMeal from '@/components/AppAccMeal.vue'
import { getReservationsFlights, getReservationsHotelsList, getReservationsMeals } from '@/http'

export default {
  name: 'SafariReservationsDetailReservations',
  props: {
    reservationData: Object,
  },
  components: {
    AppAccMeal,
    AppAccHotel,
    AppAccFlight
  },
  data() {
    return {
      is_loaded: false,
      load_error: false,
      all_hotels_reservations: [],
      meals_reservations: [],
      flights_reservations: [],
    }
  },
  computed: {
    hotels_reservations() {
      return this.all_hotels_reservations.filter(hotel => hotel.reservation_status?.code !== 'cancelled' && hotel.sales_status?.code !== 'cancel')
    },
    active_meals() {
      return this.meals_reservations.filter(meal => meal.reservation_status?.code !== 'cancelled')
    },
    cancelled_meals() {
      return this.meals_reservations.filter(meal => meal.reservation_status?.code === 'cancelled')
    },
    cancelled_hotel_ids() {
      return new Set(this.all_hotels_reservations.filter(hotel => hotel.reservation_status?.code === 'cancelled').map(hotel => hotel.id))
    },
  },
  created() {
    this.fetchAllLists()
  },
  methods: {
    mealWarning(meal) {
      if (this.cancelled_hotel_ids.has(meal.reservation_hotel)) {
        return 'Hotel reservation cancelled — meal status needs review.'
      }
      if (meal.sales_status?.code === 'cancel') {
        return 'Sales requested cancellation — meal reservation status needs review.'
      }
      return ''
    },
    fetchAllLists() {
      this.is_loaded = false
      this.load_error = false
      const baseFilter = {
        lead: +this.reservationData.lead.id,
        reservation_status_exclude: 'cancelled',
        sales_status_exclude: 'cancel',
      }
      Promise.all([
        getReservationsHotelsList({ lead: baseFilter.lead }),
        getReservationsFlights(baseFilter),
        getReservationsMeals({ lead: baseFilter.lead })
      ])
        .then(res => {
          this.all_hotels_reservations = res[0].data
          this.flights_reservations = res[1].data
          this.meals_reservations = res[2].data
        })
        .catch(() => { this.load_error = true })
        .finally(() => { this.is_loaded = true })
    },
  }
}
</script>

<style lang="sass" scoped>
.meals-description
  margin-bottom: 15px
  color: #5f5f5f

.cancelled-meals
  margin-top: 20px

  summary
    cursor: pointer
    font-weight: 500
    margin-bottom: 15px
</style>
